import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import RenderIf from 'components/RenderIf/RenderIf';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { NSButton } from 'bricks';
import { useInvestmentMemoContext } from 'views/InvestmentMemo/InvestmentMemoContext';
import TitleForm from './TitleForm/TitleForm';
import ExecutiveSummaryForm from './ExecutiveSummaryForm/ExecutiveSummaryForm';
import InvestmentHighlightsForm from './InvestmentHighlightsForm/InvestmentHighlightsForm';
import { InvestmentMemoFormData } from '../../types';
import { AISuggestions } from '../../hooks/useGenerateInvestmentMemoContent';
import {
    SUMMARY_FORMS,
    SUMMARY_FORM_NAMES,
    MEMO_SECTIONS,
    InvestmentMemoNavItem,
} from '../../constants';
import '../InvestmentMemoSections.scss';

interface IInvestmentMemoSummaryProps {
    disabled: boolean;
    formData: InvestmentMemoFormData;
    updateFormData: (section: keyof InvestmentMemoFormData, data: Partial<InvestmentMemoFormData[keyof InvestmentMemoFormData]>) => void;
    navigateToSection: (sideNavName: InvestmentMemoNavItem) => void;
    isGeneratingAIContent: boolean;
    aiSuggestions: AISuggestions;
}

const InvestmentMemoSummary = ({
    disabled,
    formData,
    updateFormData,
    navigateToSection,
    isGeneratingAIContent,
    aiSuggestions,
}: IInvestmentMemoSummaryProps) => {
    const { selectedProForma, isLoadingProForma, hasSectionError } = useInvestmentMemoContext();

    const handleContinue = () => {
        navigateToSection(InvestmentMemoNavItem.DealOverview);
    };

    return (
        <div className="d-flex justify-content-center pb-2 px-2">
            <div className="w-75">
                <div>
                    <h2 className="text-dark">{MEMO_SECTIONS[0].TITLE}</h2>
                    <p>
                        {MEMO_SECTIONS[0].DESCRIPTION}
                    </p>
                </div>

                <div className="d-flex flex-column">
                    {SUMMARY_FORMS.map(card => (
                        <div key={card.id}>
                            <ReadOnlyWrapper isReadOnly={disabled}>
                                <ExpandableSection
                                    number={card.number}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    animate
                                    disableExpandingInReadOnly
                                    isExpanded={Boolean(selectedProForma)}
                                    showWarningIcon={hasSectionError(card.id)}
                                    warningIcon={(
                                        <FontAwesomeIcon
                                            icon={faExclamationCircle}
                                            size="1x"
                                            style={{ fontSize: '0.9rem' }}
                                            className="text-danger"
                                            data-testid={`section-error-${card.id}`}
                                        />
                                    )}
                                >
                                    <RenderIf
                                        isTrue={Boolean(selectedProForma)}
                                        isLoading={isLoadingProForma}
                                        loadingMessage="Loading Northspyre AI..."
                                        shouldLoadAiLogo
                                    >
                                        <RenderIf isTrue={card.id === SUMMARY_FORM_NAMES.TITLE}>
                                            <TitleForm
                                                data={formData.title}
                                                onChange={data => updateFormData('title', data)}
                                                isLoadingTagline={isGeneratingAIContent}
                                            />
                                        </RenderIf>

                                        <RenderIf isTrue={card.id === SUMMARY_FORM_NAMES.EXECUTIVE_SUMMARY}>
                                            <ExecutiveSummaryForm
                                                data={formData.executiveSummary}
                                                onChange={data => updateFormData('executiveSummary', data)}
                                                isLoadingOpportunity={isGeneratingAIContent}
                                            />
                                        </RenderIf>

                                        <RenderIf isTrue={card.id === SUMMARY_FORM_NAMES.INVESTMENT_HIGHLIGHTS}>
                                            <InvestmentHighlightsForm
                                                data={formData.investmentHighlights}
                                                onChange={data => updateFormData('investmentHighlights', data)}
                                                isLoading={isGeneratingAIContent}
                                                aiSuggestions={aiSuggestions.highlights}
                                            />
                                        </RenderIf>
                                    </RenderIf>
                                </ExpandableSection>
                            </ReadOnlyWrapper>
                        </div>
                    ))}
                </div>
                <div className="InvestmentMemoSection__continue-button pt-2 pb-4 position-sticky d-flex justify-content-end">
                    <NSButton
                        callback={handleContinue}
                        color="primary"
                        text="Continue"
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
};

export default InvestmentMemoSummary;
